import { useState, useEffect } from 'react';
import { X, UserPlus, Edit, Check, Eye, EyeOff, Lock, ChevronDown, ChevronRight, LayoutDashboard, Table, Users, Truck, UserCheck, Building, Settings, FileText } from 'lucide-react';
import { useSelector } from 'react-redux';
import api from '../api/config';
import CustomSelect from './CustomSelect';
import './TeamModal.css';

const MAESTRO_ETAPAS = [
  {
    id: 'etapa1',
    nombre: 'Etapa 1 - Cliente',
    columnas: [
      { key: 'item', label: 'Item' },
      { key: 'cliente', label: 'Cliente' },
      { key: 'cotizacion', label: 'Cotización' },
      { key: 'p_order', label: 'Orden de Compra' },
      { key: 'item2', label: 'Item 2' },
      { key: 'descripcion', label: 'Descripción' },
      { key: 'part_number', label: 'Nro. Parte' },
      { key: 'cin', label: 'C.I.N.' },
      { key: 'delivery_date', label: 'Fecha Entrega' },
      { key: 'real_delivery', label: 'Entrega Real' },
      { key: 'cantidad', label: 'Cantidad' },
      { key: 'unidad', label: 'Unidad' },
      { key: 'pu_clp', label: 'P. Unitario CLP' },
      { key: 'total_neto', label: 'Total Neto' }
    ]
  },
  {
    id: 'etapa2',
    nombre: 'Etapa 2 - Facturación',
    columnas: [
      { key: 'guia', label: 'Guía' },
      { key: 'factura', label: 'Factura' },
      { key: 'emision_fact', label: 'Emisión' },
      { key: 'vencimiento', label: 'Vencimiento' },
      { key: 'fecha_pago', label: 'Fecha Pago' },
      { key: 'pronto_pago', label: 'Pronto Pago' },
      { key: 'utilidad_factor', label: 'Utilidad Factor' }
    ]
  },
  {
    id: 'etapa3',
    nombre: 'Etapa 3 - Proveedor',
    columnas: [
      { key: 'proveedor', label: 'Proveedor' },
      { key: 'oc_sobek', label: 'OC Proveedor' },
      { key: 'pu_proveedor', label: 'P.U. Proveedor' },
      { key: 'total_proveedor', label: 'Total Proveedor' },
      { key: 'u_del_negocio', label: 'Utilidad Negocio' },
      { key: 'factura_proveedor', label: 'Factura Proveedor' },
      { key: 'emision_prov', label: 'Emisión Prov.' },
      { key: 'vencimiento_prov', label: 'Vencimiento Prov.' },
      { key: 'fecha_pago_prov', label: 'Fecha Pago Prov.' }
    ]
  },
  {
    id: 'etapa4',
    nombre: 'Etapa 4 - Observaciones',
    columnas: [
      { key: 'observaciones', label: 'Observaciones' }
    ]
  }
];

const getDefaultMaestroConfig = () => {
  const etapas = {};
  MAESTRO_ETAPAS.forEach(etapa => {
    const columns = {};
    etapa.columnas.forEach(col => { columns[col.key] = true; });
    etapas[etapa.id] = { enabled: true, columns };
  });
  return { etapas };
};

const TeamModal = ({ isOpen, onClose, mode, member, onSubmit, companyId, isEditingSelf, currentUserRole }) => {
  const { userRole, canManageTeam, isSuperAdmin } = useSelector(state => state.auth);

  // Verificar si el usuario actual es un miembro (rol básico)
  const isCurrentUserMember = currentUserRole === 'member';
  const isCurrentUserEditor = currentUserRole === 'editor';

  // Verificar si está editando su propio perfil y no puede cambiar rol/estado
  const cannotChangeRoleAndStatus = (isEditingSelf && (isCurrentUserMember || isCurrentUserEditor)) && !isSuperAdmin;
  const [positions, setPositions] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [themeColor, setThemeColor] = useState('#c4fffc');

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);
  const [permissions, setPermissions] = useState({
    sections: {
      dashboard: true,
      maestro: false,
      clientes: false,
      proveedores: false,
      equipo: false,
      empresa: false,
      documentos: false
    },
    maestroConfig: getDefaultMaestroConfig()
  });
  const [expandedMaestro, setExpandedMaestro] = useState(false);
  const [expandedEtapas, setExpandedEtapas] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showMessage, setShowMessage] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: ''
  });
  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePassword, setChangePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Función para validar requisitos de seguridad de contraseña
  const validatePassword = (pass) => {
    const hasMinLength = pass.length >= 8;
    const hasMaxLength = pass.length <= 20;
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasNumber = /[0-9]/.test(pass);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass);

    return {
      isValid: hasMinLength && hasMaxLength && hasUpperCase && hasLowerCase && hasNumber && hasSymbol,
      hasMinLength,
      hasMaxLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSymbol
    };
  };

  const sectionConfig = {
    dashboard: { label: 'Dashboard', desc: 'Panel principal con estadísticas', icon: LayoutDashboard },
    maestro: { label: 'Maestro', desc: 'Tabla de órdenes y operaciones', icon: Table },
    clientes: { label: 'Clientes', desc: 'Gestión de clientes', icon: Users },
    proveedores: { label: 'Proveedores', desc: 'Gestión de proveedores', icon: Truck },
    equipo: { label: 'Equipo', desc: 'Gestión del equipo', icon: UserCheck },
    empresa: { label: 'Empresa', desc: 'Configuración de la empresa', icon: Building },
    documentos: { label: 'Documentos', desc: 'Gestión de documentos adjuntos', icon: FileText }
  };

  useEffect(() => {
    if (isOpen) {
      loadCatalogs();
      if (member) {
        // Establecer valores del member existente
        setSelectedRole(member.systemRole || 'member');
        setSelectedPosition(member.position || '');
        setSelectedStatus(member.status || 'active');

        // Establecer datos del formulario
        const memberData = {
          firstName: member.firstName || '',
          lastName: member.lastName || '',
          email: member.email || '',
          phone: member.phone || '',
          department: member.department || ''
        };
        setFormData(memberData);
        setOriginalData({
          ...memberData,
          position: member.position || '',
          systemRole: member.systemRole || 'member',
          status: member.status || 'active'
        });
        setHasChanges(false);

        // Cargar permisos si existen
        if (member.permissions && member.permissions.sections) {
          setPermissions({
            ...member.permissions,
            maestroConfig: member.permissions.maestroConfig || getDefaultMaestroConfig()
          });
        } else if (member.systemRole) {
          setPermissions({
            sections: {
              dashboard: true,
              maestro: false,
              clientes: false,
              proveedores: false,
              equipo: false,
              empresa: false
            },
            maestroConfig: getDefaultMaestroConfig()
          });
        }
      } else {
        // Para nuevo member, establecer valores por defecto
        setSelectedRole('member');
        setSelectedPosition('');
        setSelectedStatus('active');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          department: ''
        });
        setOriginalData(null);
        setHasChanges(false); // Para nuevo member validar que se complete la información
        setPassword('');
        setConfirmNewPassword('');
        setPermissions({
          sections: {
            dashboard: true,
            maestro: false,
            clientes: false,
            proveedores: false,
            equipo: false,
            empresa: false
          },
          maestroConfig: getDefaultMaestroConfig()
        });
      }
    } else {
      // Limpiar estados cuando se cierre el modal
      setSelectedRole('');
      setSelectedPosition('');
      setSelectedStatus('active');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        department: ''
      });
      setOriginalData(null);
      setHasChanges(false);
      setPassword('');
      setConfirmNewPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setChangePassword(false);
      setPermissions({
        sections: {
          dashboard: true,
          maestro: false,
          clientes: false,
          proveedores: false,
          equipo: false,
          empresa: false
        }
      });
    }
  }, [isOpen, member]);

  // Detectar cambios en el formulario
  useEffect(() => {
    if (mode === 'edit' && originalData) {
      const currentData = {
        ...formData,
        position: selectedPosition,
        systemRole: selectedRole,
        status: selectedStatus
      };

      const hasFormChanges = JSON.stringify(currentData) !== JSON.stringify(originalData) ||
                             (selectedRole === 'member' && JSON.stringify(permissions) !== JSON.stringify(member?.permissions || {})) ||
                             (changePassword && validatePassword(newPassword).isValid && newPassword === confirmPassword);

      setHasChanges(hasFormChanges);
    } else if (mode === 'add') {
      // Para nuevo member, validar que todos los campos requeridos estén completos
      const isFormValid = formData.firstName &&
                         formData.lastName &&
                         formData.email &&
                         selectedRole &&
                         validatePassword(password).isValid &&
                         password === confirmNewPassword;

      setHasChanges(isFormValid);
    }
  }, [formData, selectedPosition, selectedRole, selectedStatus, permissions, originalData, mode, member, changePassword, newPassword, confirmPassword, password, confirmNewPassword]);

  const loadCatalogs = async () => {
    setLoading(true);
    try {
      const [posRes, roleRes] = await Promise.all([
        api.get('/catalog/positions', { params: { companyId } }),
        api.get('/catalog/roles')
      ]);

      setPositions(posRes.data.data || []);
      setSystemRoles(roleRes.data.data || []);
    } catch (error) {
      console.error('Error loading catalogs:', error);
      // Usar roles por defecto si falla la carga
      setSystemRoles([
        { id: 1, name: 'admin', description: 'Administrador', level: 3 },
        { id: 2, name: 'editor', description: 'Editor', level: 2 },
        { id: 3, name: 'member', description: 'Miembro - Acceso básico con permisos personalizables', level: 1 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (e) => {
    const role = e.target.value;
    setSelectedRole(role);

    // Si cambia a un rol que no es member, resetear permisos
    if (role !== 'member') {
      setPermissions({
        sections: {
          dashboard: true,
          maestro: false,
          clientes: false,
          proveedores: false,
          equipo: false,
          empresa: false
        }
      });
    }
  };

  const handleToggleSection = (section) => {
    setPermissions(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: !prev.sections[section]
      }
    }));
    if (section === 'maestro') {
      setExpandedMaestro(false);
      setExpandedEtapas({});
    }
  };

  const handleToggleEtapa = (etapaId) => {
    setPermissions(prev => ({
      ...prev,
      maestroConfig: {
        ...prev.maestroConfig,
        etapas: {
          ...prev.maestroConfig.etapas,
          [etapaId]: {
            ...prev.maestroConfig.etapas[etapaId],
            enabled: !prev.maestroConfig.etapas[etapaId].enabled
          }
        }
      }
    }));
  };

  const handleToggleColumn = (etapaId, colKey) => {
    setPermissions(prev => ({
      ...prev,
      maestroConfig: {
        ...prev.maestroConfig,
        etapas: {
          ...prev.maestroConfig.etapas,
          [etapaId]: {
            ...prev.maestroConfig.etapas[etapaId],
            columns: {
              ...prev.maestroConfig.etapas[etapaId].columns,
              [colKey]: !prev.maestroConfig.etapas[etapaId].columns[colKey]
            }
          }
        }
      }
    }));
  };

  const handleToggleAllColumns = (etapaId, value) => {
    setPermissions(prev => {
      const cols = { ...prev.maestroConfig.etapas[etapaId].columns };
      Object.keys(cols).forEach(k => { cols[k] = value; });
      return {
        ...prev,
        maestroConfig: {
          ...prev.maestroConfig,
          etapas: {
            ...prev.maestroConfig.etapas,
            [etapaId]: { ...prev.maestroConfig.etapas[etapaId], columns: cols }
          }
        }
      };
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    const data = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      position: formData.get('position'),
      department: formData.get('department'),
      systemRole: formData.get('systemRole'),
      status: formData.get('status')
    };

    // Si es member, incluir los permisos personalizados
    if (data.systemRole === 'member') {
      data.permissions = permissions;
    }

    // Si se está cambiando la contraseña (modo editar)
    if (mode === 'edit' && changePassword && validatePassword(newPassword).isValid && newPassword === confirmPassword) {
      data.newPassword = newPassword;
    }

    // Si es nuevo member, incluir contraseña
    if (mode === 'add' && validatePassword(password).isValid && password === confirmNewPassword) {
      data.password = password;
    }

    const result = await onSubmit(data);
    setLoading(false);

    if (result && result.success) {
      setMessage({ type: 'success', text: mode === 'add' ? 'Miembro agregado exitosamente' : 'Miembro actualizado exitosamente' });
      setShowMessage(true);
      // Esperar 2 segundos antes de cerrar el modal
      setTimeout(() => {
        onClose();
        setShowMessage(false);
        setMessage({ type: '', text: '' });
      }, 2000);
    } else {
      setMessage({ type: 'error', text: result.message || 'Error al procesar la solicitud' });
      setShowMessage(true);
      setTimeout(() => {
        setShowMessage(false);
        setMessage({ type: '', text: '' });
      }, 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="team-modal-overlay">
      <div className="team-modal">
        <div className="team-modal-header">
          <div className="team-modal-title">
            {mode === 'add' ? <UserPlus size={24} /> : <Edit size={24} />}
            <h2>{mode === 'add' ? 'Agregar Nuevo Miembro' : 'Editar Miembro'}</h2>
          </div>
          <button className="team-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {showMessage ? (
          <div className={`modal-message ${message.type}`}>
            {message.type === 'success' ? (
              <div className="message-content">
                <Check size={64} />
                <h3>{message.text}</h3>
              </div>
            ) : (
              <div className="message-content">
                <X size={64} />
                <h3>{message.text}</h3>
              </div>
            )}
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="team-modal-form">
          <div className="team-modal-content">
            <div className="team-form-section">
              <h3>Información Personal</h3>
              <div className="team-form-row">
                <div className="team-form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Ingrese nombre"
                    required
                  />
                </div>
                <div className="team-form-field">
                  <label>Apellido</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Ingrese apellido"
                    required
                  />
                </div>
              </div>

              <div className="team-form-row">
                <div className="team-form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="correo@ejemplo.com"
                    required
                  />
                </div>
                <div className="team-form-field">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+56 9 1234 5678"
                  />
                </div>
              </div>
            </div>

            <div className="team-form-section">
              <h3>Información Laboral</h3>
              <div className="team-form-row">
                <div className="team-form-field">
                  <label>Cargo / Posición (opcional)</label>
                  <CustomSelect
                    name="position"
                    value={selectedPosition}
                    onChange={(e) => setSelectedPosition(e.target.value)}
                    options={[
                      { value: '', label: 'Seleccione un cargo' },
                      ...positions.map(pos => ({ value: pos.name, label: pos.name }))
                    ]}
                    placeholder="Seleccione un cargo"
                  />
                </div>
                <div className="team-form-field">
                  <label>Departamento (Opcional)</label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    placeholder="Ej: Ventas, Finanzas, Operaciones"
                  />
                </div>
              </div>

              <div className="team-form-row">
                <div className="team-form-field">
                  <label>Rol de Permisos</label>
                  <CustomSelect
                    name="systemRole"
                    value={selectedRole}
                    onChange={handleRoleChange}
                    options={systemRoles
                      .filter(role => {
                        // Filtrar superadmin
                        if (role.name === 'superadmin') return false;

                        // Si es editor, solo puede asignar roles de member y editor
                        if (currentUserRole === 'editor') {
                          return role.name === 'member' || role.name === 'editor';
                        }

                        // Si es admin o superadmin, puede asignar todos los roles excepto superadmin
                        return true;
                      })
                      .sort((a, b) => b.level - a.level)
                      .map(role => ({ value: role.name, label: role.description }))
                    }
                    placeholder="Seleccione un rol"
                    required={true}
                    disabled={cannotChangeRoleAndStatus}
                    title={cannotChangeRoleAndStatus ? 'No puedes cambiar tu propio rol' : ''}
                  />
                </div>
                <div className="team-form-field">
                  <label>Estado</label>
                  <CustomSelect
                    name="status"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    options={[
                      { value: 'active', label: 'Activo' },
                      { value: 'vacation', label: 'Vacaciones' },
                      { value: 'inactive', label: 'Inactivo' }
                    ]}
                    placeholder="Seleccione un estado"
                    disabled={cannotChangeRoleAndStatus}
                    title={cannotChangeRoleAndStatus ? 'No puedes cambiar tu propio estado' : ''}
                  />
                </div>
              </div>
            </div>

            {/* Sección de contraseña */}
            {mode === 'add' ? (
              <div className="team-form-section">
                <h3>Contraseña</h3>
                <p className="password-instruction">
                  <strong>La contraseña debe contener: 8-20 caracteres, mayúsculas, minúsculas, números y símbolos (!@#$%^&*)</strong>
                </p>
                <div className="team-form-row">
                  <div className="team-form-field">
                    <label>Contraseña</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Ejemplo: MiClave@2024"
                        minLength="8"
                        maxLength="20"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div className="team-form-field">
                    <label>Confirmar Contraseña</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="Repetir contraseña"
                        minLength="8"
                        maxLength="20"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
                {password && (
                  <div className="password-requirements">
                    <p className="requirements-title">Requisitos de seguridad:</p>
                    <ul>
                      <li className={validatePassword(password).hasMinLength ? 'valid' : 'invalid'}>
                        ✓ Mínimo 8 caracteres
                      </li>
                      <li className={validatePassword(password).hasMaxLength ? 'valid' : 'invalid'}>
                        ✓ Máximo 20 caracteres
                      </li>
                      <li className={validatePassword(password).hasUpperCase ? 'valid' : 'invalid'}>
                        ✓ Al menos una mayúscula
                      </li>
                      <li className={validatePassword(password).hasLowerCase ? 'valid' : 'invalid'}>
                        ✓ Al menos una minúscula
                      </li>
                      <li className={validatePassword(password).hasNumber ? 'valid' : 'invalid'}>
                        ✓ Al menos un número
                      </li>
                      <li className={validatePassword(password).hasSymbol ? 'valid' : 'invalid'}>
                        ✓ Al menos un símbolo (!@#$%^&*)
                      </li>
                    </ul>
                  </div>
                )}
                {password && confirmNewPassword && password !== confirmNewPassword && (
                  <p style={{ color: '#dc2626', fontSize: '14px', margin: '8px 0 0 0', fontWeight: '600' }}>
                    ⚠️ Las contraseñas no coinciden
                  </p>
                )}
              </div>
            ) : (
              /* Sección de cambio de contraseña solo para editar */
              <div className="team-form-section">
                <h3>Seguridad</h3>
                <div className="password-change-section">
                  {!changePassword ? (
                    <button
                      type="button"
                      className="btn-change-password"
                      onClick={() => setChangePassword(true)}
                      style={{
                        backgroundColor: 'white',
                        color: '#000',
                        border: `2px solid ${themeColor}`
                      }}
                    >
                      <Lock size={18} />
                      Cambiar Contraseña
                    </button>
                  ) : (
                    <>
                      <div className="password-change-header">
                        <p><strong>La contraseña debe contener: 8-20 caracteres, mayúsculas, minúsculas, números y símbolos (!@#$%^&*)</strong></p>
                        <button
                          type="button"
                          className="btn-cancel-password"
                          onClick={() => {
                            setChangePassword(false);
                            setNewPassword('');
                            setConfirmPassword('');
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                      <div className="team-form-row">
                        <div className="team-form-field">
                          <label>Nueva Contraseña</label>
                          <div className="password-input-wrapper">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Ejemplo: MiClave@2024"
                              minLength="8"
                              maxLength="20"
                            />
                            <button
                              type="button"
                              className="password-toggle"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>
                        <div className="team-form-field">
                          <label>Confirmar Nueva Contraseña</label>
                          <div className="password-input-wrapper">
                            <input
                              type={showConfirmNewPassword ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Repetir contraseña"
                              minLength="8"
                              maxLength="20"
                            />
                            <button
                              type="button"
                              className="password-toggle"
                              onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                            >
                              {showConfirmNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>
                      </div>
                      {newPassword && (
                        <div className="password-requirements">
                          <p className="requirements-title">Requisitos de seguridad:</p>
                          <ul>
                            <li className={validatePassword(newPassword).hasMinLength ? 'valid' : 'invalid'}>
                              ✓ Mínimo 8 caracteres
                            </li>
                            <li className={validatePassword(newPassword).hasMaxLength ? 'valid' : 'invalid'}>
                              ✓ Máximo 20 caracteres
                            </li>
                            <li className={validatePassword(newPassword).hasUpperCase ? 'valid' : 'invalid'}>
                              ✓ Al menos una mayúscula
                            </li>
                            <li className={validatePassword(newPassword).hasLowerCase ? 'valid' : 'invalid'}>
                              ✓ Al menos una minúscula
                            </li>
                            <li className={validatePassword(newPassword).hasNumber ? 'valid' : 'invalid'}>
                              ✓ Al menos un número
                            </li>
                            <li className={validatePassword(newPassword).hasSymbol ? 'valid' : 'invalid'}>
                              ✓ Al menos un símbolo (!@#$%^&*)
                            </li>
                          </ul>
                        </div>
                      )}
                      {newPassword && confirmPassword && newPassword !== confirmPassword && (
                        <p style={{ color: '#dc2626', fontSize: '14px', margin: '8px 0 0 0', fontWeight: '600' }}>
                          ⚠️ Las contraseñas no coinciden
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Sección de permisos solo si es member Y el usuario actual puede editarlos */}
            {selectedRole === 'member' && (!isCurrentUserMember || isSuperAdmin) && (
              <div className="team-form-section permissions-section">
                <h3>Permisos de Acceso</h3>
                <p className="permissions-description">
                  Configura a qué secciones tendrá acceso este miembro
                </p>
                <div className="perm-sections-list">
                  {Object.entries(sectionConfig).map(([key, config]) => {
                    const IconComp = config.icon;
                    const isActive = permissions.sections[key];
                    return (
                      <div key={key} className={`perm-section-card ${isActive ? 'active' : ''}`}>
                        <div className="perm-section-row" onClick={() => handleToggleSection(key)}>
                          <div className="perm-section-icon" style={isActive ? { background: themeColor } : {}}>
                            <IconComp size={18} />
                          </div>
                          <div className="perm-section-info">
                            <span className="perm-section-label">{config.label}</span>
                            <span className="perm-section-desc">{config.desc}</span>
                          </div>
                          <div className={`perm-switch ${isActive ? 'on' : ''}`} style={isActive ? { backgroundColor: themeColor } : {}}>
                            <div className="perm-switch-thumb" />
                          </div>
                        </div>

                        {key === 'maestro' && isActive && (
                          <div className="perm-maestro-area">
                            <button
                              type="button"
                              className="perm-maestro-trigger"
                              onClick={() => setExpandedMaestro(!expandedMaestro)}
                            >
                              <Settings size={14} />
                              <span>Configurar etapas y columnas visibles</span>
                              <ChevronDown size={14} className={`perm-chevron ${expandedMaestro ? 'open' : ''}`} />
                            </button>

                            {expandedMaestro && (
                              <div className="perm-maestro-body">
                                {MAESTRO_ETAPAS.map(etapa => {
                                  const etapaConfig = permissions.maestroConfig?.etapas?.[etapa.id] || { enabled: true, columns: {} };
                                  const isEtapaExpanded = expandedEtapas[etapa.id];
                                  const enabledCount = etapa.columnas.filter(c => etapaConfig.columns?.[c.key] !== false).length;
                                  const etapaColors = {
                                    etapa1: '#10b981', etapa2: '#f43f5e',
                                    etapa3: '#f59e0b', etapa4: '#8b5cf6'
                                  };

                                  return (
                                    <div key={etapa.id} className={`perm-etapa ${etapaConfig.enabled !== false ? '' : 'disabled'}`}>
                                      <div className="perm-etapa-row">
                                        <div className="perm-etapa-left">
                                          <div className="perm-etapa-dot" style={{ background: etapaColors[etapa.id] }} />
                                          <span className="perm-etapa-title">{etapa.nombre}</span>
                                          {etapaConfig.enabled !== false && (
                                            <span className="perm-etapa-count">{enabledCount}/{etapa.columnas.length}</span>
                                          )}
                                        </div>
                                        <div className="perm-etapa-right">
                                          {etapaConfig.enabled !== false && (
                                            <button
                                              type="button"
                                              className="perm-etapa-cols-btn"
                                              onClick={() => setExpandedEtapas(prev => ({ ...prev, [etapa.id]: !prev[etapa.id] }))}
                                            >
                                              <ChevronDown size={14} className={`perm-chevron ${isEtapaExpanded ? 'open' : ''}`} />
                                            </button>
                                          )}
                                          <div
                                            className={`perm-switch sm ${etapaConfig.enabled !== false ? 'on' : ''}`}
                                            style={etapaConfig.enabled !== false ? { backgroundColor: etapaColors[etapa.id] } : {}}
                                            onClick={() => handleToggleEtapa(etapa.id)}
                                          >
                                            <div className="perm-switch-thumb" />
                                          </div>
                                        </div>
                                      </div>

                                      {etapaConfig.enabled !== false && isEtapaExpanded && (
                                        <div className="perm-cols-panel">
                                          <div className="perm-cols-header">
                                            <span>Columnas visibles</span>
                                            <div className="perm-cols-quick">
                                              <button type="button" onClick={() => handleToggleAllColumns(etapa.id, true)}>Todas</button>
                                              <button type="button" onClick={() => handleToggleAllColumns(etapa.id, false)}>Ninguna</button>
                                            </div>
                                          </div>
                                          <div className="perm-cols-grid">
                                            {etapa.columnas.map(col => {
                                              const colActive = etapaConfig.columns?.[col.key] !== false;
                                              return (
                                                <label key={col.key} className={`perm-col-chip ${colActive ? 'on' : ''}`}
                                                  style={colActive ? { borderColor: etapaColors[etapa.id], background: `${etapaColors[etapa.id]}12` } : {}}>
                                                  <input
                                                    type="checkbox"
                                                    checked={colActive}
                                                    onChange={() => handleToggleColumn(etapa.id, col.key)}
                                                  />
                                                  <span className="perm-col-dot" style={colActive ? { background: etapaColors[etapa.id] } : {}} />
                                                  <span>{col.label}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {!showMessage && (
          <div className="team-modal-footer">
            <button type="button" className="team-btn-cancel" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className={`team-btn-submit ${mode === 'edit' && !hasChanges ? 'disabled' : ''}`}
              disabled={mode === 'edit' && !hasChanges}
              title={mode === 'edit' && !hasChanges ? 'No hay cambios para guardar' : ''}
              style={{
                backgroundColor: (mode === 'edit' && !hasChanges) ? '#ccc' : themeColor,
                color: '#000',
                border: '2px solid #000'
              }}
            >
              {mode === 'add' ? 'Agregar Miembro' : 'Guardar Cambios'}
            </button>
          </div>
          )}
        </form>
        )}
      </div>
    </div>
  );
};

export default TeamModal;